import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Landing.css";
import "./LandingAdditions.css";

const DEMO_SCRIPTS = [
  {
    trade: "HVAC",
    business: "Summit Air",
    messages: [
      {
        type: "lead",
        text: "Hi! Our AC just stopped blowing cold air. It is 88 degrees in here!",
      },
      {
        type: "bot",
        text: "Oh no! That sounds miserable — let me get you help fast. Is this for your home or a business?",
      },
      { type: "lead", text: "Home. We need someone out ASAP." },
      {
        type: "bot",
        text: "Got it — emergency home visit. What is your name and address?",
      },
      { type: "lead", text: "Mike Torres, 4821 Elm St" },
      {
        type: "bot",
        text: "Thanks Mike! I have a technician available today between 2–4pm. Want me to lock that in?",
      },
      { type: "lead", text: "Yes please!" },
      {
        type: "bot",
        text: "Done! ✅ Technician booked for today 2–4pm. You will get a text confirmation shortly. Stay cool!",
      },
    ],
  },
  {
    trade: "Plumbing",
    business: "FlowRight Plumbing",
    messages: [
      {
        type: "lead",
        text: "Do you guys do water heater installs? Ours is leaking.",
      },
      {
        type: "bot",
        text: "Yes we do — and a leaking water heater should not wait. Is it gas or electric?",
      },
      { type: "lead", text: "Gas. It is about 12 years old." },
      {
        type: "bot",
        text: "At 12 years, replacement is usually the smart move. A standard 50-gallon gas install runs $1,800–$2,400. Want a free on-site quote tomorrow?",
      },
      { type: "lead", text: "That price range works. Tomorrow morning?" },
      {
        type: "bot",
        text: "Perfect — I have 9am or 11am open tomorrow. Which works better?",
      },
      { type: "lead", text: "9am" },
      {
        type: "bot",
        text: "Booked! ✅ Free estimate tomorrow at 9am. I will text you the technician's name tonight.",
      },
    ],
  },
  {
    trade: "Roofing",
    business: "IronPeak Roofing",
    messages: [
      {
        type: "lead",
        text: "We had hail last night. Not sure if our roof is damaged.",
      },
      {
        type: "bot",
        text: "Hail damage is sneaky — I am glad you are checking. Most policies cover a free inspection after a storm like that. What is your address?",
      },
      { type: "lead", text: "7710 Cypress Grove Ln" },
      {
        type: "bot",
        text: "Thanks! I can send our storm team for a free 21-point inspection Thursday or Friday. Which day is better?",
      },
      { type: "lead", text: "Friday" },
      {
        type: "bot",
        text: "Friday it is ✅ Free inspection booked. If we find damage, we will document everything for your insurance claim.",
      },
    ],
  },
  {
    trade: "Electrical",
    business: "VoltEdge Electric",
    messages: [
      { type: "lead", text: "How much to install an EV charger in my garage?" },
      {
        type: "bot",
        text: "Great question — most Level 2 installs run $600–$1,200 depending on your panel. Do you know if your panel has room for a 240V breaker?",
      },
      { type: "lead", text: "Not sure honestly" },
      {
        type: "bot",
        text: "No problem — that is exactly what our free estimate covers. I can book a tech to check your panel this week. What is your name?",
      },
      { type: "lead", text: "Sarah Kim" },
      {
        type: "bot",
        text: "Thanks Sarah! How about Wednesday between 10am–12pm for the free estimate?",
      },
      { type: "lead", text: "Works for me" },
      {
        type: "bot",
        text: "Locked in ✅ Wednesday 10am–12pm. I will send a reminder the night before.",
      },
    ],
  },
];

function ChatDemo({ onClose }) {
  const [scriptIndex, setScriptIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [showTyping, setShowTyping] = useState(false);
  const scrollRef = useRef(null);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const playScript = (index) => {
    clearTimers();
    const script = DEMO_SCRIPTS[index];
    setMessages([]);
    setShowTyping(false);
    let delay = 400;
    script.messages.forEach((msg) => {
      if (msg.type === "bot") {
        timersRef.current.push(setTimeout(() => setShowTyping(true), delay));
        delay += 900;
      }
      timersRef.current.push(
        setTimeout(() => {
          setShowTyping(false);
          setMessages((prev) => [...prev, msg]);
        }, delay),
      );
      delay += msg.type === "bot" ? 1400 : 1100;
    });
  };

  useEffect(() => {
    playScript(scriptIndex);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptIndex]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, showTyping]);

  const script = DEMO_SCRIPTS[scriptIndex];

  return (
    <div className="chat-demo-wrapper">
      <p className="chat-demo-label">Live demo — {script.trade}</p>
      <div className="chat-demo">
        <div className="chat-demo__header">
          <div className="chat-demo__bot-icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
            </svg>
          </div>
          <div className="chat-demo__header-info">
            <p className="chat-demo__name">{script.business}</p>
            <p className="chat-demo__status">
              <span className="chat-demo__status-dot"></span>
              Chatflo is online
            </p>
          </div>
          {onClose && (
            <button
              className="chat-demo__close"
              onClick={onClose}
              aria-label="Close demo"
            >
              ×
            </button>
          )}
        </div>
        <div className="chat-demo__messages" ref={scrollRef}>
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`chat-demo__msg chat-demo__msg--${msg.type}`}
            >
              <div className="chat-demo__bubble">{msg.text}</div>
            </div>
          ))}
          {showTyping && (
            <div className="chat-demo__typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
        </div>
      </div>
      <div className="chat-demo__nav">
        <button
          className="chat-demo__arrow"
          onClick={() =>
            setScriptIndex(
              (scriptIndex - 1 + DEMO_SCRIPTS.length) % DEMO_SCRIPTS.length,
            )
          }
          aria-label="Previous demo"
        >
          ‹
        </button>
        <div className="chat-demo__dots">
          {DEMO_SCRIPTS.map((s, i) => (
            <button
              key={s.trade}
              className={`chat-demo__dot ${i === scriptIndex ? "active" : ""}`}
              onClick={() => setScriptIndex(i)}
              aria-label={`Show ${s.trade} demo`}
            />
          ))}
        </div>
        <button
          className="chat-demo__arrow"
          onClick={() =>
            setScriptIndex((scriptIndex + 1) % DEMO_SCRIPTS.length)
          }
          aria-label="Next demo"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function Landing() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [leadStatus, setLeadStatus] = useState("");
  const [leadLoading, setLeadLoading] = useState(false);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLeadLoading(true);
    setLeadStatus("");
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, source: "landing-cta" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Something went wrong");
      setLeadStatus("You are in! Check your inbox for next steps.");
      setEmail("");
    } catch (err) {
      setLeadStatus(err.message);
    } finally {
      setLeadLoading(false);
    }
  };

  const features = [
    {
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      ),
      title: "24/7 Instant Response",
      desc: "Every visitor gets an answer in under 60 seconds — nights, weekends, holidays.",
      bullets: [
        "No more missed after-hours leads",
        "Answers from your real services & pricing",
      ],
    },
    {
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      ),
      title: "Automatic Booking",
      desc: "Chatflo qualifies the lead and books the job straight into your calendar.",
      bullets: [
        "Books estimates, repairs & installs",
        "Sends confirmations & reminders",
      ],
    },
    {
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
      ),
      title: "Lead Qualification",
      desc: "Asks the right questions so you only talk to serious buyers.",
      bullets: [
        "Captures name, phone & job details",
        "Scores urgency automatically",
      ],
    },
    {
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
      ),
      title: "Works On Any Website",
      desc: "One line of code. No rebuild, no plugins, no developer needed.",
      bullets: ["Live in 4 days", "We handle the entire setup"],
    },
    {
      icon: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      title: "You Stay In Control",
      desc: "Set boundaries, approve messaging, and jump into any conversation live.",
      bullets: ["Human handoff anytime", "Full transcript for every chat"],
    },
  ];

  const steps = [
    {
      number: "1",
      title: "A 15 minute fit call",
      desc: "We tear down your site's after-hours experience live and show you where visitors slip away. If the math works, we move forward.",
    },
    {
      number: "2",
      title: "Free 4-day install",
      desc: "We install Chatflo on your website for you. Most shops are live within 4 days. It costs you nothing to start.",
    },
    {
      number: "3",
      title: "Wake up to booked jobs",
      desc: "Chatflo answers your website visitors, qualifies them, and books the job while you are on the tools. You pay zero until it books your 3rd qualified appointment.",
    },
  ];

  const plans = [
    {
      name: "Founding Partner",
      desc: "$997/month locked for life. $1,497/month when founding closes.",
      price: "$997",
      conversations: "40-60 conversations / mo",
      overage: "$2.50 per additional conversation",
      features: [
        "$0 to start, install free",
        "First invoice after the system books your 3rd appointment",
        "4-day install",
        "24/7 website answering, qualifying and booking",
      ],
      cta: "Get your free leak audit",
      plan: "Founding Partner",
      popular: false,
    },
  ];

  const faqs = [
    {
      q: "How fast can I go live?",
      a: "Most businesses are live within 4 days. We handle the entire setup and train Chatflo on your services, pricing, and FAQs. You don\u2019t touch a thing.",
    },
    {
      q: "When do I pay?",
      a: "You pay $0 to start. Your first invoice comes after Chatflo books your 3rd qualified appointment from your website traffic. If it has not booked 3 within 60 days, it stays live and works free until it does.",
    },
    {
      q: "What happens when Chatflo doesn\u2019t know an answer?",
      a: "It collects the customer\u2019s contact details and alerts you right away. No lead ever hits a dead end.",
    },
    {
      q: "Will it work with my website?",
      a: "Yes. Chatflo works with any website — paste in one line of code, or we install it for you during onboarding.",
    },
    {
      q: "What counts as a conversation?",
      a: "One conversation is a full chat session with a customer, from first message to booking or close. If you go over your plan\u2019s volume, extra conversations are $2.50 each — always shown transparently, never a surprise.",
    },
  ];

  return (
    <div className="landing-page">
      {/* NAV */}
      <nav className="landing-nav">
        <div className="landing-container">
          <div className="nav-logo">
            <img
              src="/chatflo-logo.png"
              alt="Chatflo logo"
              className="nav-logo-img"
            />
            <span>Chatflo</span>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? "open" : ""}`}>
            <button
              className="nav-link"
              onClick={() => scrollToSection("features")}
            >
              Features
            </button>
            <button
              className="nav-link"
              onClick={() => scrollToSection("pricing")}
            >
              Pricing
            </button>
            <button className="nav-link" onClick={() => scrollToSection("faq")}>
              FAQ
            </button>
            <button className="nav-link" onClick={() => navigate("/login")}>
              Login
            </button>
            <a className="nav-cta" href="https://calendly.com/chatflo-tech/30min" target="_blank" rel="noopener noreferrer">
              Get your free leak audit
            </a>
          </div>
          <button
            className="nav-mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 12h18M3 6h18M3 18h18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* HERO */}
      <header className="landing-hero">
        <div className="landing-container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <span className="hero-badge-dot"></span>
                Free leak audit · No credit card
              </div>
              <h1 className="hero-title">THE AFTER-HOURS CLOSER</h1>
              <p className="hero-subtitle">
                Turn the website traffic you're already paying for into booked
                appointments. Without hiring another employee.
              </p>
              <div className="hero-ctas">
                <button
                  className="btn-primary"
                  onClick={() => window.open("https://calendly.com/chatflo-tech/30min", "_blank", "noopener,noreferrer")}
                >
                  Get your free leak audit
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
                <a className="btn-outline" href="#how-it-works">See how it works</a>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-value">24/7</span>
                  <span className="hero-stat-label">Instant response</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-value">&lt;60s</span>
                  <span className="hero-stat-label">Average reply time</span>
                </div>
                <div className="hero-stat">
                  <span className="hero-stat-value">4 days</span>
                  <span className="hero-stat-label">Free installation</span>
                </div>
              </div>
            </div>
            <div className="hero-demo">
              <div className="hero-mockup">
                <ChatDemo />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* TRUSTED BY */}
      <section className="landing-trusted">
        <div className="landing-container">
          <div className="trusted-content">
            <p className="trusted-label">
              Built for service businesses like yours
            </p>
            <div className="trusted-logos">
              <span className="trusted-logo">HVAC</span>
              <span className="trusted-logo">Plumbing</span>
              <span className="trusted-logo">Electrical</span>
              <span className="trusted-logo">Roofing</span>
              <span className="trusted-logo">Pest Control</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="landing-features" id="features">
        <div className="landing-container">
          <div className="section-header">
            <p className="section-label">Features</p>
            <h2 className="section-title">
              Your best office manager, on every page
            </h2>
            <p className="section-subtitle">
              Chatflo does the follow-up work nobody has time for — so every
              click has a shot at becoming a customer.
            </p>
          </div>
          <div className="features-grid">
            {features.map((f) => (
              <div key={f.title} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
                <ul className="feature-list">
                  {f.bullets.map((b) => (
                    <li key={b}>
                      <svg
                        className="feature-check"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="landing-how-it-works" id="how-it-works">
        <div className="landing-container">
          <div className="section-header">
            <p className="section-label">How it works</p>
            <h2 className="section-title">
              Live in 4 days. Nothing to install yourself.
            </h2>
          </div>
          <div className="steps-grid">
            {steps.map((s) => (
              <div key={s.number} className="step-card">
                <div className="step-number">{s.number}</div>
                <h3 className="step-title">{s.title}</h3>
                <p className="step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="landing-pricing" id="pricing">
        <div className="landing-container">
          <div className="section-header">
            <p className="section-label">Pricing</p>
            <h2 className="section-title">Simple, honest pricing</h2>
            <p className="section-subtitle">
              One booked job pays for months of Chatflo.
            </p>
            <p className="pricing-roi-link">
              <a className="btn-outline" href="#math">
                See what missed calls are costing you →
              </a>
            </p>
          </div>
          <div className="pricing-grid">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`pricing-card ${plan.popular ? "popular" : ""}`}
              >
                {plan.popular && (
                  <div className="popular-badge">Most Popular</div>
                )}
                <h3 className="pricing-name">{plan.name}</h3>
                <p className="pricing-desc">{plan.desc}</p>
                <div className="pricing-amount">
                  <span className="pricing-dollar">{plan.price}</span>
                  {plan.price.startsWith("$") && (
                    <span className="pricing-period">/month</span>
                  )}
                </div>
                <p className="pricing-conversations">{plan.conversations}</p>
                <p className="pricing-overage">{plan.overage}</p>
                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f}>
                      <svg
                        className="feature-check"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                {plan.plan ? (
                  <button
                    className={`pricing-btn ${plan.popular ? "pricing-btn-primary" : "pricing-btn-outline"}`}
                    onClick={() => window.open("https://calendly.com/chatflo-tech/30min", "_blank", "noopener,noreferrer")}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <a
                    className="pricing-btn pricing-btn-link"
                    href="https://calendly.com/chatflo-tech/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  >
                    {plan.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE STACK */}
      <section className="landing-pricing landing-compact" id="math">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">Here's the math.</h2>
          </div>
          <ul className="value-stack-list">
            <li>24/7 website answering, qualifying, and booking: $2,500/mo</li>
            <li>Monthly caught-revenue report: $250/mo</li>
          </ul>
          <p className="value-stack-total">$2,750/month in value.</p>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="landing-pricing landing-compact">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">The 3-Appointment Guarantee</h2>
          </div>
          <p className="section-subtitle">Pay zero until Chatflo books 3 qualified appointments from your website traffic. If it has not booked 3 within 60 days live, it stays live and works free until it does. Walk away anytime owing nothing. What counts as a qualified appointment is defined in writing on our fit call before anything is installed.</p>
        </div>
      </section>

      {/* FOUNDING TERMS */}
      <section className="landing-pricing landing-compact">
        <div className="landing-container">
          <div className="section-header">
            <h2 className="section-title">Founding Partner</h2>
          </div>
          <p className="section-subtitle">The first 5 HVAC shops get the $1,000 implementation waived, $997/month locked for life, personal installation oversight, and weekly check-ins during month one. Installs are scheduled in the order shops sign.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-faq" id="faq">
        <div className="landing-container">
          <div className="section-header">
            <p className="section-label">FAQ</p>
            <h2 className="section-title">Questions, answered</h2>
            <p className="section-subtitle">
              What owners usually ask before getting started.
            </p>
          </div>
          <div className="faq-list">
            {faqs.map((f) => (
              <div key={f.q} className="faq-item">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="landing-cta" id="cta">
        <div className="landing-container">
          <div className="cta-content">
            <h2 className="cta-title">
              Never miss a lead. Never lose a booking.
            </h2>
            <p className="cta-subtitle">
              Get your free leak audit and see exactly where your website loses after-hours jobs.
            </p>
      <a className="cta-submit" href="https://calendly.com/chatflo-tech/30min" target="_blank" rel="noopener noreferrer">Get My Free Leak Audit</a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="nav-logo">
                <img
                  src="/chatflo-logo.png"
                  alt="Chatflo logo"
                  className="nav-logo-img"
                />
                <span>Chatflo</span>
              </div>
              <p className="footer-tagline">
                AI chat &amp; lead automation for service businesses.
              </p>
              <p className="footer-contact">
                <a href="mailto:chatflo.tech@gmail.com">
                  chatflo.tech@gmail.com
                </a>
              </p>
            </div>
            <div className="footer-links">
              <button onClick={() => scrollToSection("features")}>
                Features
              </button>
              <button onClick={() => scrollToSection("pricing")}>
                Pricing
              </button>
              <button onClick={() => scrollToSection("faq")}>FAQ</button>
              <button onClick={() => navigate("/login")}>Login</button>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copy">
              © {new Date().getFullYear()} Chatflo. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
